import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LayoutGrid, List, Plus, Search, TrendingUp, DollarSign, Wallet } from 'lucide-react';
import BlogCard from './components/BlogCard';
import BlogDetail from './components/BlogDetail';
import CreatePostModal from './components/CreatePostModal';
import { BlogPost, Category } from './types';
import { CATEGORIES, INITIAL_BLOGS } from './constants';

const BlogApp: React.FC = () => {
    const [blogs, setBlogs] = useState<BlogPost[]>(INITIAL_BLOGS);
    const [selectedCategory, setSelectedCategory] = useState<Category | 'All'>('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [selectedBlog, setSelectedBlog] = useState<BlogPost | null>(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const resultsRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const url = new URL(window.location.href);
        const categoryParam = url.searchParams.get('category');
        if (categoryParam && CATEGORIES.includes(categoryParam as Category)) {
            setSelectedCategory(categoryParam as Category);
        }
    }, []);

    useEffect(() => {
        const url = new URL(window.location.href);
        if (selectedCategory === 'All') {
            url.searchParams.delete('category');
        } else {
            url.searchParams.set('category', selectedCategory);
        }
        window.history.replaceState({}, '', url.toString());
    }, [selectedCategory]);

    const filteredBlogs = useMemo(() => {
        return blogs
            .filter((blog) => {
                const matchesCategory = selectedCategory === 'All' || blog.category === selectedCategory;
                const matchesSearch =
                    blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    blog.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
                return matchesCategory && matchesSearch;
            })
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [blogs, selectedCategory, searchQuery]);

    const handleAddBlog = (newBlog: BlogPost) => {
        setBlogs((prev) => [newBlog, ...prev]);
        setIsCreateModalOpen(false);
    };

    const handleCategoryChange = (category: Category | 'All') => {
        setSelectedCategory(category);
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="min-h-screen flex flex-col bg-slate-50 text-gray-900">
            <header className="sticky top-0 z-50 glass border-b border-emerald-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <button
                            type="button"
                            className="flex items-center space-x-2"
                            onClick={() => handleCategoryChange('All')}
                        >
                            <div className="bg-emerald-600 p-2 rounded-lg">
                                <TrendingUp className="text-white w-6 h-6" />
                            </div>
                            <span className="text-2xl font-extrabold text-gray-900 tracking-tight">
                                FIRERIFY<span className="text-emerald-600">BLOG</span>
                            </span>
                        </button>

                        <div className="hidden md:flex items-center space-x-6">
                            <a
                                href="#"
                                className="text-gray-600 hover:text-emerald-600 font-medium transition-colors"
                            >
                                Dashboard
                            </a>
                            <a href="#" className="text-emerald-600 font-bold border-b-2 border-emerald-600">
                                Blog
                            </a>
                            <a
                                href="#"
                                className="text-gray-600 hover:text-emerald-600 font-medium transition-colors"
                            >
                                Calculators
                            </a>
                            <button
                                type="button"
                                onClick={() => setIsCreateModalOpen(true)}
                                className="bg-emerald-600 text-white px-4 py-2 rounded-full font-bold flex items-center gap-2 hover:bg-emerald-700 transition-all shadow-md active:scale-95"
                            >
                                <Plus size={18} /> New Post
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="flex-grow">
                <section className="bg-gradient-to-br from-emerald-50 via-white to-sky-50 py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h1 className="text-4xl md:text-6xl font-black text-gray-900 mb-6 leading-tight">
                            Master Your Money, <br />
                            <span className="text-emerald-600">Accelerate Your FIRE.</span>
                        </h1>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8 font-medium">
                            Daily insights on wealth management, personalized for the Firerify community. Join 10,000+
                            achieving financial independence.
                        </p>

                        <div className="max-w-xl mx-auto flex items-center bg-white rounded-full shadow-lg p-2 border border-emerald-100">
                            <div className="pl-4 text-gray-400">
                                <Search size={20} />
                            </div>
                            <input
                                type="text"
                                placeholder="Search wealth tips, FIRE strategies..."
                                className="w-full px-4 py-2 outline-none text-gray-700 bg-transparent"
                                value={searchQuery}
                                onChange={(event) => setSearchQuery(event.target.value)}
                            />
                            <button
                                type="button"
                                className="bg-emerald-600 text-white px-6 py-2 rounded-full font-bold hover:bg-emerald-700 transition-colors"
                            >
                                Search
                            </button>
                        </div>
                    </div>
                </section>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 mb-8">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div className="flex items-center gap-2 overflow-x-auto pb-2 w-full md:w-auto no-scrollbar">
                            <button
                                type="button"
                                onClick={() => handleCategoryChange('All')}
                                className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-bold transition-all ${
                                    selectedCategory === 'All'
                                        ? 'bg-emerald-600 text-white shadow-lg'
                                        : 'bg-white text-gray-600 hover:bg-emerald-50 border border-gray-200'
                                }`}
                            >
                                All Stories
                            </button>
                            {CATEGORIES.map((category) => (
                                <button
                                    type="button"
                                    key={category}
                                    onClick={() => handleCategoryChange(category)}
                                    className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-bold transition-all ${
                                        selectedCategory === category
                                            ? 'bg-emerald-600 text-white shadow-lg'
                                            : 'bg-white text-gray-600 hover:bg-emerald-50 border border-gray-200'
                                    }`}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>

                        <div className="flex items-center gap-4 bg-white p-1 rounded-lg border border-gray-200 shadow-sm">
                            <button
                                type="button"
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded-md transition-colors ${
                                    viewMode === 'grid' ? 'bg-emerald-50 text-emerald-600' : 'text-gray-400 hover:text-gray-600'
                                }`}
                            >
                                <LayoutGrid size={20} />
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded-md transition-colors ${
                                    viewMode === 'list' ? 'bg-emerald-50 text-emerald-600' : 'text-gray-400 hover:text-gray-600'
                                }`}
                            >
                                <List size={20} />
                            </button>
                        </div>
                    </div>
                </div>

                <div ref={resultsRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
                    {filteredBlogs.length > 0 ? (
                        <div
                            className={
                                viewMode === 'grid'
                                    ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'
                                    : 'flex flex-col gap-6'
                            }
                        >
                            {filteredBlogs.map((blog) => (
                                <BlogCard
                                    key={blog.id}
                                    blog={blog}
                                    viewMode={viewMode}
                                    onClick={() => setSelectedBlog(blog)}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-20 bg-white rounded-2xl border-2 border-dashed border-gray-200">
                            <div className="flex justify-center mb-4">
                                <Search size={48} className="text-gray-300" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-700">No blog posts found</h3>
                            <p className="text-gray-500">Try adjusting your filters or search query.</p>
                        </div>
                    )}
                </div>
            </main>

            <footer className="bg-gray-900 text-white py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
                        <div className="col-span-1 md:col-span-2">
                            <div className="flex items-center space-x-2 mb-6">
                                <div className="bg-emerald-600 p-2 rounded-lg">
                                    <TrendingUp className="text-white w-5 h-5" />
                                </div>
                                <span className="text-xl font-extrabold tracking-tight">
                                    FIRERIFY<span className="text-emerald-500">BLOG</span>
                                </span>
                            </div>
                            <p className="text-gray-400 max-w-sm mb-6">
                                The ultimate companion for your financial independence journey. Track assets, plan
                                retirement, and learn from experts.
                            </p>
                            <div className="flex space-x-4">
                                <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-emerald-600 cursor-pointer transition-colors">
                                    <TrendingUp size={20} />
                                </div>
                                <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-emerald-600 cursor-pointer transition-colors">
                                    <DollarSign size={20} />
                                </div>
                                <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-emerald-600 cursor-pointer transition-colors">
                                    <Wallet size={20} />
                                </div>
                            </div>
                        </div>

                        <div>
                            <h4 className="text-lg font-bold mb-6">Resources</h4>
                            <ul className="space-y-4 text-gray-400">
                                <li>
                                    <a href="#" className="hover:text-emerald-500 transition-colors">
                                        FIRE Calculator
                                    </a>
                                </li>
                                <li>
                                    <a href="#" className="hover:text-emerald-500 transition-colors">
                                        Net Worth Tracker
                                    </a>
                                </li>
                                <li>
                                    <a href="#" className="hover:text-emerald-500 transition-colors">
                                        Asset Allocation
                                    </a>
                                </li>
                                <li>
                                    <a href="#" className="hover:text-emerald-500 transition-colors">
                                        Success Stories
                                    </a>
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-lg font-bold mb-6">Newsletter</h4>
                            <p className="text-gray-400 mb-4 text-sm">Get the weekly wealth report.</p>
                            <div className="flex bg-gray-800 rounded-lg p-1">
                                <input
                                    type="email"
                                    placeholder="Your email"
                                    className="bg-transparent px-3 py-2 outline-none w-full text-sm"
                                />
                                <button
                                    type="button"
                                    className="bg-emerald-600 text-white px-4 py-2 rounded-md text-sm font-bold hover:bg-emerald-700 transition-colors"
                                >
                                    Join
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-500">
                        <p>© 2024 Firerify App. All rights reserved.</p>
                        <div className="flex space-x-6 mt-4 md:mt-0">
                            <a href="#" className="hover:text-white">
                                Privacy Policy
                            </a>
                            <a href="#" className="hover:text-white">
                                Terms of Service
                            </a>
                        </div>
                    </div>
                </div>
            </footer>

            {selectedBlog && <BlogDetail blog={selectedBlog} onClose={() => setSelectedBlog(null)} />}

            {isCreateModalOpen && (
                <CreatePostModal onClose={() => setIsCreateModalOpen(false)} onSubmit={handleAddBlog} />
            )}
        </div>
    );
};

export default BlogApp;
